import React from 'react';
import { Copy, Check } from 'lucide-react';

const VariableInsertionPanel = ({ onInsertVariable, onInsertLogo, onInsertWhatsApp }) => {
  const [copiedVariable, setCopiedVariable] = React.useState(null);

  const variables = [
    {
      category: 'Customer Info',
      items: [
        { name: 'Customer Name', value: '{{name}}', description: 'Full name of the customer' },
        { name: 'Email', value: '{{email}}', description: 'Customer email address' },
        { name: 'Phone', value: '{{phone}}', description: 'Customer phone number' },
        { name: 'Business Unit', value: '{{businessUnit}}', description: 'Salon, Spa, Gym, or Others' },
        { name: 'Loyalty Points', value: '{{loyaltyPoints}}', description: 'Customer loyalty points balance' }
      ]
    },
    {
      category: 'Actions',
      items: [
        { name: 'Unsubscribe Link', value: '{{unsubscribeLink}}', description: 'Required for email compliance', required: true }
      ]
    }
  ];

  const handleCopyVariable = (value) => {
    navigator.clipboard.writeText(value);
    setCopiedVariable(value);
    setTimeout(() => setCopiedVariable(null), 2000);
  };

  const handleInsertVariable = (value) => {
    if (onInsertVariable) {
      onInsertVariable(value);
    }
  };

  const handleInsertLogo = () => {
    if (onInsertLogo) {
      onInsertLogo();
    }
  };

  const handleInsertWhatsApp = () => {
    if (onInsertWhatsApp) {
      onInsertWhatsApp();
    }
  };

  return (
    <div className="variable-panel bg-gray-50 border border-gray-200 rounded-lg p-4 h-full overflow-y-auto">
      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
        📋 Available Variables
      </h3>

      {/* Variables by Category */}
      {variables.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-6">
          <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
            {category.category}
          </h4>
          <div className="space-y-2">
            {category.items.map((variable, index) => (
              <div
                key={index}
                className="group bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition cursor-pointer"
                title={variable.description}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700">
                        {variable.name}
                      </span>
                      {variable.required && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <code className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded mt-1 inline-block">
                      {variable.value}
                    </code>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-2">
                  {variable.description}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleInsertVariable(variable.value)}
                    className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition"
                  >
                    Insert
                  </button>
                  <button
                    onClick={() => handleCopyVariable(variable.value)}
                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition flex items-center gap-1"
                  >
                    {copiedVariable === variable.value ? (
                      <>
                        <Check className="w-3 h-3" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Branding Section */}
      <div className="mb-6">
        <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
          Branding
        </h4>
        <button
          onClick={handleInsertLogo}
          className="w-full bg-purple-50 border border-purple-200 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-100 transition text-sm font-medium flex items-center justify-center gap-2"
        >
          <span className="text-lg">🖼️</span>
          Insert Logo
        </button>
        <p className="text-xs text-gray-500 mt-1 text-center">
          Adds /logo.png (Ella's Place logo)
        </p>
      </div>

      {/* Contact Section */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
          Contact
        </h4>
        <button
          onClick={handleInsertWhatsApp}
          className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg hover:bg-green-100 transition text-sm font-medium flex items-center justify-center gap-2"
        >
          <span className="text-lg">💬</span>
          WhatsApp Link
        </button>
        <p className="text-xs text-gray-500 mt-1 text-center">
          +234 905 544 4495
        </p>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-6">
        <p className="text-xs text-blue-800">
          <strong>💡 Tip:</strong> Click "Insert" to add a variable at your cursor position in the email editor. Variables will be replaced with actual customer data when sending.
        </p>
      </div>
    </div>
  );
};

export default VariableInsertionPanel;